/**
 * real-google change: pure state mapping for Admin -> System operations -> Integrations -> Google
 * Cloud (CHANGE-real-google section 2). Everything the page derives from the masked configuration
 * and the form lives here so it can be unit tested without React.
 *
 * Secrets are write-only: the form holds a pasted value until it is saved, then clears it; the
 * configuration only ever carries `*SecretSet` booleans.
 */
import { api } from '@agency/shared';
import type { ChipTone } from '@agency/ui';
import { fmtTimestamp } from '@agency/ui/src/format.js';

export type GoogleConfig = api.GoogleIntegrationConfig;

export interface GoogleForm {
  desktopClientId: string;
  /** Pasted value, write-only; empty means "keep the stored secret". */
  desktopClientSecret: string;
  webClientId: string;
  webClientSecret: string;
  hostedDomain: string;
  workforceSignIn: boolean;
}

export const EMPTY_FORM: GoogleForm = { desktopClientId: '', desktopClientSecret: '', webClientId: '', webClientSecret: '', hostedDomain: '', workforceSignIn: false };

/** Form values for a loaded configuration (secrets never come back, so their fields start empty). */
export function formFromConfig(cfg: GoogleConfig): GoogleForm {
  return {
    desktopClientId: cfg.desktopClientId ?? '',
    desktopClientSecret: '',
    webClientId: cfg.webClientId ?? '',
    webClientSecret: '',
    hostedDomain: cfg.hostedDomain ?? '',
    workforceSignIn: cfg.workforceSignIn,
  };
}

export const STATUS_LABEL: Record<api.GoogleIntegrationStatus, string> = { configured: 'Configured', not_configured: 'Not configured' };
export const STATUS_TONE: Record<api.GoogleIntegrationStatus, ChipTone> = { configured: 'ok', not_configured: 'warn' };

/** Copy for each stable failure code of the Test action. */
export const TEST_ERROR_LABEL: Record<api.GoogleIntegrationTestError, string> = {
  not_configured: 'Desktop client id and secret are not set',
  client_id_format: 'Client id does not match the Google format',
  discovery_unreachable: 'Google OpenID discovery unreachable',
  discovery_invalid: 'Google OpenID discovery document invalid',
  issuer_mismatch: 'Discovery issuer differs from accounts.google.com',
  unexpected: 'Unexpected error',
};

/**
 * Secret field hint: `set · updated YYYY-MM-DD HH:MMZ` when a secret is stored (the configuration
 * carries one update time for the whole record), `not set` otherwise.
 */
export function secretHint(set: boolean, updatedAt: string | null): string {
  if (!set) return 'not set';
  return updatedAt ? `set · updated ${fmtTimestamp(updatedAt)}` : 'set';
}

/** One-line result of a Test action: `ok · issuer … · time` or `failed · reason · time`. */
export function testResultLine(result: api.GoogleIntegrationTestResponse): { tone: 'ok' | 'brand'; text: string } {
  const at = fmtTimestamp(result.testedAt);
  if (result.ok) return { tone: 'ok', text: `ok · issuer ${result.discoveryIssuer ?? '--'} · ${at}` };
  return { tone: 'brand', text: `failed · ${TEST_ERROR_LABEL[result.error ?? 'unexpected']} · ${at}` };
}

/** Last-test summary for the status card from the stored configuration. */
export function lastTestSummary(cfg: Pick<GoogleConfig, 'lastTestedAt' | 'lastTestResult'>): string {
  if (!cfg.lastTestedAt) return 'never tested';
  return `${cfg.lastTestResult ?? '--'} · ${fmtTimestamp(cfg.lastTestedAt)}`;
}

/** Header subtitle: facts with a middot, no adjectives. */
export function headerSubtitle(cfg: GoogleConfig): string {
  const parts = [`Google Cloud · ${STATUS_LABEL[cfg.status].toLowerCase()}`];
  parts.push(cfg.hostedDomain ? `domain ${cfg.hostedDomain}` : 'no hosted domain');
  parts.push(cfg.workforceSignIn ? 'Google Workspace sign-in on' : 'Google Workspace sign-in off');
  parts.push(cfg.lastTestedAt ? `last test ${cfg.lastTestResult ?? '--'} ${fmtTimestamp(cfg.lastTestedAt)}` : 'never tested');
  return parts.join(' · ');
}

/**
 * Whether the web client secret would be stored after the form is saved: a pasted value, or a stored
 * one that survives because the web client id is not being cleared (clearing an id drops its secret).
 */
export function webSecretAvailable(form: Pick<GoogleForm, 'webClientId' | 'webClientSecret'>, cfg: Pick<GoogleConfig, 'webClientSecretSet'> | null): boolean {
  if (form.webClientSecret.length > 0) return true;
  return Boolean(cfg?.webClientSecretSet) && form.webClientId.trim().length > 0;
}

/**
 * "Use Google Workspace for sign-in" is enabled only when the web client id, its secret and the
 * hosted domain will all be present after saving (the server rejects the switch otherwise).
 */
export function canEnableWorkforceSignIn(form: GoogleForm, cfg: Pick<GoogleConfig, 'webClientSecretSet'> | null): boolean {
  return form.webClientId.trim().length > 0 && webSecretAvailable(form, cfg) && form.hostedDomain.trim().length > 0;
}

/** Client id format warning for a non-empty id that does not look like a Google OAuth client id. */
export function clientIdWarning(value: string): string | null {
  const v = value.trim();
  if (v.length === 0 || api.isGoogleOAuthClientIdFormat(v)) return null;
  return 'Expected <digits>-<hash>.apps.googleusercontent.com';
}

function normalizeId(value: string): string | null {
  const v = value.trim();
  return v.length === 0 ? null : v;
}

function normalizeDomain(value: string): string | null {
  const v = value.trim().toLowerCase();
  return v.length === 0 ? null : v;
}

/**
 * Partial update body: only the keys that differ from the stored configuration, secrets whenever a
 * value was pasted. The switch is forced off when its prerequisites are missing. Returns null when
 * nothing would change.
 */
export function buildUpdateRequest(form: GoogleForm, cfg: GoogleConfig): api.GoogleIntegrationUpdateRequest | null {
  const body: api.GoogleIntegrationUpdateRequest = {};
  const desktopClientId = normalizeId(form.desktopClientId);
  if (desktopClientId !== cfg.desktopClientId) body.desktopClientId = desktopClientId;
  if (form.desktopClientSecret.length > 0) body.desktopClientSecret = form.desktopClientSecret;
  const webClientId = normalizeId(form.webClientId);
  if (webClientId !== cfg.webClientId) body.webClientId = webClientId;
  if (form.webClientSecret.length > 0) body.webClientSecret = form.webClientSecret;
  const hostedDomain = normalizeDomain(form.hostedDomain);
  if (hostedDomain !== cfg.hostedDomain) body.hostedDomain = hostedDomain;
  const workforceSignIn = form.workforceSignIn && canEnableWorkforceSignIn(form, cfg);
  if (workforceSignIn !== cfg.workforceSignIn) body.workforceSignIn = workforceSignIn;
  return Object.keys(body).length === 0 ? null : body;
}

export type GoogleFieldErrors = Partial<Record<keyof GoogleForm, string>>;

/** Field-level errors from validating the update body against the shared request schema. */
export function fieldErrors(body: api.GoogleIntegrationUpdateRequest | null): GoogleFieldErrors {
  if (!body) return {};
  const parsed = api.GoogleIntegrationUpdateRequestSchema.safeParse(body);
  if (parsed.success) return {};
  const out: GoogleFieldErrors = {};
  for (const issue of parsed.error.issues) {
    const key = issue.path[0];
    if (typeof key !== 'string' || !(key in EMPTY_FORM)) continue;
    const field = key as keyof GoogleForm;
    if (!out[field]) out[field] = friendlyIssue(field, issue.message);
  }
  return out;
}

function friendlyIssue(field: keyof GoogleForm, message: string): string {
  if (field === 'desktopClientSecret' || field === 'webClientSecret') return /small|>=|at least/i.test(message) ? 'Secret must be at least 8 characters' : message;
  if (field === 'hostedDomain') return 'Expected a domain name such as getagency.com';
  return message;
}

/** Steps the administrator follows in Google Cloud; mirrors README "Production Google setup". */
export const SETUP_CHECKLIST: readonly { step: string; href: string | null }[] = [
  { step: 'Create a Google Cloud project', href: 'https://console.cloud.google.com/projectcreate' },
  { step: 'Enable the Gmail, Google Docs, Google Sheets and Google Drive APIs', href: 'https://console.cloud.google.com/apis/library' },
  { step: 'Configure the OAuth consent screen as Internal and add the scopes listed here', href: 'https://console.cloud.google.com/apis/credentials/consent' },
  { step: 'Create an OAuth client of type Desktop app; paste its id and secret above', href: 'https://console.cloud.google.com/apis/credentials' },
  { step: 'Optional: create a Web application client with redirect http://localhost:4400/admin/auth/callback for Google Workspace sign-in', href: 'https://console.cloud.google.com/apis/credentials' },
  { step: 'Save, run Test, then connect Google from Settings on the desktop', href: null },
];
